import { isNetworkError } from "@/Helpers/isNetworkError";
import { isUnauthorizedError } from "@/Helpers/isUnauthorizedError";
import { useOnce } from "@/Hooks/useOnce";
import { useUserService } from "@/Services/UserService";
import { useState } from "react";

interface BookerNames {
  // Resolves a shipment's `bookedBy` (a user id) to that user's display name.
  // Returns undefined when the booker can't be resolved — e.g. an empty
  // `bookedBy`, or a user who has since left the company and is no longer in
  // the company user list. Callers should hide the field in that case.
  bookerNameFor: (bookedBy: string | undefined) => string | undefined;
}

// Fetches the company's users once and exposes a resolver from `bookedBy`
// (user id) to display name. Mirrors how the dashboard already matches
// `shipment.bookedBy` against the company user list (see useDashboardUserSelect).
export function useBookerNames(): BookerNames {
  const userService = useUserService();
  const [namesByUserId, setNamesByUserId] = useState<Record<string, string>>(
    {},
  );

  // On a 401 the names stay unresolved and useSessionExpiry handles the
  // session. With no answer from the API at all, the names stay unresolved
  // too. Any other failure is rethrown so it still reaches Sentry.
  useOnce(async function () {
    let users;
    try {
      users = await userService.listUsersForCompany();
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      if (isNetworkError(error)) {
        console.warn("Could not load the company's users", { error });
        return;
      }
      throw error;
    }
    const map: Record<string, string> = {};
    users.forEach((user) => {
      map[user.userId] = user.name;
    });
    setNamesByUserId(map);
  });

  function bookerNameFor(bookedBy: string | undefined): string | undefined {
    if (bookedBy === undefined || bookedBy === "") {
      return undefined;
    }

    return namesByUserId[bookedBy];
  }

  return { bookerNameFor };
}
